/**
 * sentryUtils.ts — Scrubbing Sentry + tag helpers (SPEC-2026-06-08-sentry-sdk-integration §4).
 *
 * beforeSend hook passé à Sentry.init() côté frontend.
 * Masque les adresses wallet, supprime cookies / Authorization, ne transmet pas user.email.
 * Tag helpers (chain_id, is_admin, payment_provider) à appeler depuis les flows dédiés.
 *
 * §9 DRY — une seule définition, importée par src/index.tsx.
 * §7 §8 — aucune clé / secret / token ne quitte le navigateur vers Sentry.
 */

import * as Sentry from '@sentry/react';

// Regexp : adresse Ethereum 0x suivi de 40 chars hex (checksummed ou lowercase)
const WALLET_RE = /0x[0-9a-fA-F]{40}/g;

// ---- Tag helpers -------------------------------------------------------
// Appeler depuis les flows correspondants :
//   setSentryChainId      → walletConnect / network switch (ex: reducers/user.js)
//   setSentryIsAdmin      → login / setUser (ex: reducers/user.js case setUser)
//   setSentryPaymentProvider → initiation paiement (ex: StripeModal, NowPaymentsModal)

/** Positionne chain_id sur le scope Sentry (ex: '43114', '43113'). */
export function setSentryChainId(chainId: string | number): void {
  if (!chainId) return;
  Sentry.setTag('chain_id', String(chainId));
}

/** Positionne is_admin sur le scope Sentry ('true' / 'false'). */
export function setSentryIsAdmin(isAdmin: boolean): void {
  Sentry.setTag('is_admin', isAdmin ? 'true' : 'false');
}

/** Positionne payment_provider sur le scope Sentry ('stripe' | 'paypal' | 'nowpayments' | 'crypto'). */
export function setSentryPaymentProvider(provider: string): void {
  if (!provider) return;
  Sentry.setTag('payment_provider', provider);
}

// ---- Breadcrumb helpers -------------------------------------------------------
// Enrichissent le fil d'audit Sentry pour les 3 flows critiques.
// À appeler AVANT chaque action clé (pas après — le breadcrumb doit précéder l'erreur éventuelle).

/** Breadcrumb : initiation d'un paiement (stripe / paypal / nowpayments / crypto). */
export function addBreadcrumbPaymentInit(provider: string, extra?: Record<string, unknown>): void {
  setSentryPaymentProvider(provider);
  Sentry.addBreadcrumb({
    category: 'payment',
    message: `Payment init — ${provider}`,
    level: 'info',
    data: extra,
  });
}

/** Breadcrumb : connexion wallet réussie (EarniWallet SUCCESS). */
export function addBreadcrumbWalletConnect(method?: string): void {
  Sentry.addBreadcrumb({
    category: 'wallet',
    message: `Wallet connected${method ? ` (${method})` : ''}`,
    level: 'info',
  });
}

/** Breadcrumb : opération de mint initiée (tile / tile-group / collectible). */
export function addBreadcrumbMint(type: 'tile' | 'tile-group' | 'collectible', id: string | number, extra?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    category: 'mint',
    message: `Mint initiated — ${type} #${id}`,
    level: 'info',
    data: extra,
  });
}

/**
 * Remplace toutes les occurrences d'une adresse wallet par `[wallet-redacted]`
 * dans une chaîne, ou retourne `val` tel quel si ce n'est pas une string.
 */
function redactWallets(val: unknown): unknown {
  if (typeof val === 'string') {
    return val.replace(WALLET_RE, '[wallet-redacted]');
  }
  return val;
}

// SPEC-2026-06-30 D9 — masque la VALEUR des paramètres de query sensibles (clé conservée).
const SCRUBBED_QUERY_KEYS = ['token', 'code', 'password', 'key', 'secret', 'signature', 'sig', 'access_token', 'api_key'];
function scrubQueryString(qs: string): string {
  if (!qs) return qs;
  const re = new RegExp('(^|[?&])((?:' + SCRUBBED_QUERY_KEYS.join('|') + ')=)[^&#]*', 'gi');
  return qs.replace(re, '$1$2[Scrubbed]');
}

/**
 * scrubbedEvent — beforeSend Sentry frontend.
 *
 * Appliqué AVANT l'envoi réseau ; retourner `null` supprime l'event.
 * Retourner l'event (éventuellement muté) le transmet.
 *
 * Opérations :
 *   1. Masquer adresses wallet dans message + exception values + breadcrumbs
 *   2. Supprimer cookies (request.cookies)
 *   3. Supprimer header Authorization (request.headers)
 *   4. Supprimer user.email (PII)
 */
// Signature compatible avec le type `beforeSend` de @sentry v8
// ((event, hint) => event | null). On garde un typage souple (`any`) car le corps
// mute des champs optionnels (exception.values, breadcrumbs, request…) ; un typage
// strict ErrorEvent imposerait des narrowings non pertinents pour du scrubbing défensif.
export function scrubbedEvent(event: any, _hint?: any): any | null {
  // --- 1a. Scrub message principal ---
  if (event.message && typeof event.message === 'string') {
    event.message = event.message.replace(WALLET_RE, '[wallet-redacted]');
  }

  // --- 1b. Scrub exception values (stack traces) ---
  if (Array.isArray(event.exception?.values)) {
    event.exception.values = event.exception.values.map((exc: any) => {
      if (exc.value && typeof exc.value === 'string') {
        exc.value = exc.value.replace(WALLET_RE, '[wallet-redacted]');
      }
      return exc;
    });
  }

  // --- 1c. Scrub breadcrumbs (messages de navigation / XHR) ---
  if (Array.isArray(event.breadcrumbs?.values)) {
    event.breadcrumbs.values = event.breadcrumbs.values.map((crumb: any) => {
      if (crumb.message && typeof crumb.message === 'string') {
        crumb.message = crumb.message.replace(WALLET_RE, '[wallet-redacted]');
      }
      if (crumb.data) {
        // Scrub les clés data qui pourraient contenir un wallet
        Object.keys(crumb.data).forEach((k) => {
          crumb.data[k] = redactWallets(crumb.data[k]);
        });
      }
      return crumb;
    });
  }

  // --- 2. Supprimer cookies (ne jamais envoyer les cookies session / auth) ---
  if (event.request) {
    delete event.request.cookies;

    // --- 3. Supprimer header Authorization ---
    if (event.request.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
    }

    // --- 3b. SPEC-2026-06-30 D9 — masquer les secrets en query string (?token=…, ?code=…) ---
    if (event.request.url) event.request.url = scrubQueryString(event.request.url);
    const qs = (event.request as { query_string?: unknown }).query_string;
    if (typeof qs === 'string') {
      (event.request as { query_string?: string }).query_string = scrubQueryString(qs);
    }
  }

  // --- 4. Supprimer user.email (PII) ---
  if (event.user) {
    delete event.user.email;
  }

  return event;
}
